import React from 'react';
import type { ReactNode } from 'react';
import { CarFront, Keyboard } from 'lucide-react';
import { useManualVehicleSearchWithQuery, useVinVrmSearchWithQuery } from '../../hooks';
import { useVehicleSearchContext } from '../../hooks/useVehicleSearchContext';
import { Button, Tooltip } from '@shared/components';
import { VinVrmSearchForm } from '../VinVrmVehicleSearch/VinVrmSearchForm';
import { ManualVehicleSearch } from '../ManualVehicleSearch/ManualVehicleSearch';
import { DerivativeResults } from '../ManualVehicleSearch/DerivativeResults';
import { VehicleResults } from '../VinVrmVehicleSearch/VehicleResults';
import { VehicleSearchProvider } from '../../providers/VehicleSearchProvider';
import type { Vehicle } from '../../types/vinvrmsearch.types';
import type { VehicleDerivative } from '../../types/manualsearch.types';

interface VehicleSearchProps {
    onSelectVehicle?: (vehicle: Vehicle | VehicleDerivative) => void;
    selectedVehicle?: Vehicle | VehicleDerivative | null; // Optional prop to allow external selection
    showMotHistory?: boolean;
    renderLookupNotice?: (vehicle: Vehicle) => ReactNode;
}

const VehicleSearchContent: React.FC<VehicleSearchProps> = ({
    onSelectVehicle,
    showMotHistory = false,
    renderLookupNotice,
}) => {
    const { activeSearchMethod, setActiveSearchMethod } = useVehicleSearchContext();
    const manualSearchHook = useManualVehicleSearchWithQuery();
    const vinVrmSearchHook = useVinVrmSearchWithQuery();
    const { vehicleDerivatives, onVehicleSelect: onDerivativeSelect } = manualSearchHook;
    const { vehicles, onVehicleSelect: onVinVrmSelect } = vinVrmSearchHook;
    const searchMethod = activeSearchMethod || 'vinvrm';

    const onVinVrmSelectHandler =
        onSelectVehicle &&
        ((vehicle: Vehicle) => {
            onVinVrmSelect(vehicle);
            onSelectVehicle(vehicle);
        });

    const onDerivativeSelectHandler =
        onSelectVehicle &&
        ((derivative: VehicleDerivative) => {
            onDerivativeSelect(derivative);
            onSelectVehicle(derivative);
        });

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap md:justify-end content-center">
                <Tooltip content="VIN or VRN Search">
                    <Button
                        onClick={() => setActiveSearchMethod('vinvrm')}
                        className="rounded-l-md"
                        rounded={false}
                        variant={searchMethod === 'vinvrm' ? 'primary' : 'outline'}
                        size="xs"
                        ariaLabel="VIN or VRN Search"
                        startIcon={<CarFront size={18} />}
                    />
                </Tooltip>
                <Tooltip content="Manual Vehicle Search">
                    <Button
                        onClick={() => setActiveSearchMethod('manual')}
                        className="relative rounded-r-md"
                        rounded={false}
                        variant={searchMethod === 'manual' ? 'primary' : 'outline'}
                        size="xs"
                        ariaLabel="Manual Vehicle Search"
                        endIcon={<Keyboard size={18} />}
                    />
                </Tooltip>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1 space-y-4">
                    {/* Search Forms */}
                    {searchMethod === 'vinvrm' ? (
                        <VinVrmSearchForm {...vinVrmSearchHook} />
                    ) : (
                        <ManualVehicleSearch {...manualSearchHook} />
                    )}
                    {searchMethod === 'vinvrm' && vehicles[0] && renderLookupNotice?.(vehicles[0])}
                </div>
                <div className="lg:col-span-2">
                    {/* Results */}
                    {searchMethod === 'vinvrm' && (
                        <VehicleResults
                            vehicles={vehicles}
                            onSelect={onVinVrmSelectHandler}
                            isLoading={vinVrmSearchHook.isSearchLoading}
                            searchError={vinVrmSearchHook.searchError}
                            showMotHistory={showMotHistory}
                        />
                    )}
                    {searchMethod === 'manual' && (
                        <DerivativeResults
                            derivatives={vehicleDerivatives}
                            hasSearched={manualSearchHook.hasSearched}
                            onSelect={onDerivativeSelectHandler}
                            isLoading={manualSearchHook.isSearchLoading}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export const VehicleSearch: React.FC<VehicleSearchProps> = ({
    onSelectVehicle,
    showMotHistory = false,
    renderLookupNotice,
}) => {
    return (
        <VehicleSearchProvider>
            <VehicleSearchContent
                onSelectVehicle={onSelectVehicle}
                showMotHistory={showMotHistory}
                renderLookupNotice={renderLookupNotice}
            />
        </VehicleSearchProvider>
    );
};
